// ============================================
// PurchaseStatusBadge - Order Status Badge
// 4 status variants: in_transit, delivered, pending, cancelled
// ============================================

import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

export type PurchaseOrderStatus = 'in_transit' | 'delivered' | 'pending' | 'cancelled';

interface PurchaseStatusBadgeProps {
  status: BackendStockImportStatus;
  className?: string;
}

// ============================================
// Status Configurations
// ============================================

const STATUS_CONFIG: Record<
  BackendStockImportStatus,
  { label: string; bgColor: string; textColor: string }
> = {
  DRAFT: { label: 'DRAFT', bgColor: '#E1E2ED', textColor: '#434655' },
  SUBMITTED: { label: 'SUBMITTED', bgColor: 'rgba(37, 99, 235, 0.1)', textColor: '#004AC6' },
  APPROVED: { label: 'APPROVED', bgColor: 'rgba(99, 102, 241, 0.12)', textColor: '#4338CA' },
  REJECTED: { label: 'REJECTED', bgColor: '#FFDAD6', textColor: '#BA1A1A' },
  RECEIVING: { label: 'RECEIVING', bgColor: 'rgba(148, 55, 0, 0.1)', textColor: '#943700' },
  COMPLETED: { label: 'COMPLETED', bgColor: 'rgba(34, 150, 83, 0.12)', textColor: '#219653' },
  CANCELLED: { label: 'CANCELLED', bgColor: '#FFDAD6', textColor: '#BA1A1A' },
};

// ============================================
// Component
// ============================================

export function PurchaseStatusBadge({
  status,
  className,
}: PurchaseStatusBadgeProps) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.DRAFT;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1',
        'text-[10px] font-bold uppercase tracking-[0.25px]',
        className
      )}
      style={{
        backgroundColor: config.bgColor,
        color: config.textColor,
      }}
    >
      {config.label}
    </span>
  );
}

// ============================================
// Helper: Map backend status to UI status
// ============================================

export type BackendStockImportStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'RECEIVING'
  | 'COMPLETED'
  | 'CANCELLED';

export function mapBackendStatusToUI(
  backendStatus: BackendStockImportStatus
): PurchaseOrderStatus {
  const statusMap: Record<BackendStockImportStatus, PurchaseOrderStatus> = {
    DRAFT: 'pending',
    SUBMITTED: 'pending',
    APPROVED: 'in_transit',
    REJECTED: 'cancelled',
    RECEIVING: 'in_transit',
    COMPLETED: 'delivered',
    CANCELLED: 'cancelled',
  };

  return statusMap[backendStatus] || 'pending';
}

/**
 * Map a coarse UI status back to the backend enum values for server-side
 * filtering. UI groups map to multiple backend statuses (comma-separated,
 * which the API supports, e.g. ?status=DRAFT,SUBMITTED).
 */
export function mapUIStatusToBackend(uiStatus: string | null | undefined): string | undefined {
  if (!uiStatus) return undefined;
  const map: Record<string, string> = {
    pending: 'DRAFT,SUBMITTED',
    in_transit: 'APPROVED,RECEIVING',
    delivered: 'COMPLETED',
    cancelled: 'CANCELLED',
  };
  return map[uiStatus];
}
